import React, { useState, useCallback, useEffect } from 'react';
import { ClientPerformanceFilters } from '../../types';
import CalendarDatePicker from './CalendarDatePicker';

interface ReportFiltersProps {
  filters: ClientPerformanceFilters;
  onFilterChange: (filters: ClientPerformanceFilters) => void;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({ filters, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState<ClientPerformanceFilters>(filters);
  const [isExpanded, setIsExpanded] = useState(false); // Start with advanced filters collapsed

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleInputChange = useCallback((
    field: keyof ClientPerformanceFilters,
    value: string | number | undefined
  ) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    onFilterChange(localFilters);
  }, [localFilters, onFilterChange]);

  const handleResetFilters = useCallback(() => {
    // Get yesterday's date
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    const defaultFilters: ClientPerformanceFilters = {
      dateFrom: yesterdayStr,
      dateTo: yesterdayStr
    };
    setLocalFilters(defaultFilters);
    onFilterChange(defaultFilters);
  }, [onFilterChange]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  }, [handleApplyFilters]);

  return (
    <div className="report-filters">
      <div className="filters-header">
        <h3>Filters</h3>
      </div>

      <div className="filters-content">
        <div className="primary-filters">
          <div className="filter-group date-selector-group">
            <label>Date Range</label>
            <CalendarDatePicker
              dateFrom={localFilters.dateFrom}
              dateTo={localFilters.dateTo}
              onChange={(dateFrom, dateTo) => {
                const newFilters = { ...localFilters, dateFrom, dateTo };
                setLocalFilters(newFilters);
              }}
              onKeyPress={handleKeyPress}
            />
          </div>

          <div className="filter-group search-group">
            <label htmlFor="search">Search</label>
            <input
              id="search"
              type="text"
              placeholder="Company, domain, or tags..."
              value={localFilters.searchTerm || ''}
              onChange={(e) => handleInputChange('searchTerm', e.target.value)}
              onKeyPress={handleKeyPress}
              className="filter-input search-input"
            />
          </div>

          <div className="filter-actions">
            <button
              onClick={handleApplyFilters}
              className="filter-button apply-button"
            >
              Apply Filters
            </button>
            <button
              onClick={handleResetFilters}
              className="filter-button reset-button"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="advanced-filters-toggle">
          <button
            className="toggle-advanced"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Hide advanced filters' : 'Show advanced filters'}
          >
            <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
            Advanced Filters
          </button>
        </div>

        {isExpanded && (
          <div className="advanced-filters">
            <div className="advanced-filters-row">
              <div className="filter-group domain-group">
                <label htmlFor="domain">Domain</label>
                <input
                  id="domain"
                  type="text"
                  placeholder="example.com"
                  value={localFilters.domain || ''}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="filter-input domain-input"
                />
              </div>

              <div className="filter-group rate-group">
                <label htmlFor="min-inbox">Min Inbox Rate (%)</label>
                <input
                  id="min-inbox"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={localFilters.minInboxRate || ''}
                  onChange={(e) => handleInputChange('minInboxRate', e.target.value ? Number(e.target.value) : undefined)}
                  onKeyPress={handleKeyPress}
                  className="filter-input rate-input"
                />
              </div>

              <div className="filter-group rate-group">
                <label htmlFor="max-spam">Max Spam Rate (%)</label>
                <input
                  id="max-spam"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="100"
                  value={localFilters.maxSpamRate || ''}
                  onChange={(e) => handleInputChange('maxSpamRate', e.target.value ? Number(e.target.value) : undefined)}
                  onKeyPress={handleKeyPress}
                  className="filter-input rate-input"
                />
              </div>
              
              <div className="advanced-spacer"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ReportFilters);